
import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { storage } from '../../../services/StorageService';

interface HUDPanelProps {
  children: ReactNode;
  initialX?: number;
  initialY?: number;
  initialWidth?: number;
  initialHeight?: number;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  resizable?: boolean; // Enable resize handles (default: true)
  className?: string;
  style?: React.CSSProperties;
  dragHandleSelector?: string; // CSS selector for the handle element (optional)
  onDragEnd?: (x: number, y: number) => void;
  onResizeEnd?: (width: number, height: number) => void;
  id?: string; // For storage persistence
}

export const HUDPanel: React.FC<HUDPanelProps> = ({
  children,
  initialX = 0,
  initialY = 0,
  initialWidth,
  initialHeight,
  minWidth = 100,
  minHeight = 50,
  maxWidth = 800,
  maxHeight = 600,
  resizable = true,
  className,
  style,
  dragHandleSelector,
  onDragEnd,
  onResizeEnd,
  id
}) => {
  // Try to restore position and size from storage if ID is provided
  const loadState = () => {
    if (id) {
      const state = storage.getObjectRaw<{x: number, y: number, width: number, height: number}>(`draggable_state_${id}`);
      if (state) {
        return state;
      }
    }
    return { 
      x: initialX, 
      y: initialY, 
      width: initialWidth, 
      height: initialHeight 
    };
  };

  const [position, setPosition] = useState(() => {
    const state = loadState();
    return { x: state.x, y: state.y };
  });
  const [size, setSize] = useState(() => {
    const state = loadState();
    return { width: state.width, height: state.height };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const panelRef = useRef<HTMLDivElement>(null);

  // Save state to storage
  const saveState = () => {
    if (id) {
      storage.setObjectRaw(`draggable_state_${id}`, {
        x: position.x,
        y: position.y,
        width: size.width,
        height: size.height
      });
    }
  };

  useEffect(() => {
    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        saveState();
        onDragEnd?.(position.x, position.y);
      }
      if (isResizing) {
        setIsResizing(false);
        saveState();
        onResizeEnd?.(size.width || 0, size.height || 0);
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y
        });
      }
      if (isResizing) {
        const newWidth = Math.max(minWidth, Math.min(maxWidth, resizeStart.w + (e.clientX - resizeStart.x)));
        const newHeight = Math.max(minHeight, Math.min(maxHeight, resizeStart.h + (e.clientY - resizeStart.y)));
        setSize({ width: newWidth, height: newHeight });
      }
    };

    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragOffset, resizeStart, id, position, size, onDragEnd, onResizeEnd, minWidth, minHeight, maxWidth, maxHeight]);

  const handleMouseDown = (e: React.MouseEvent) => {
    // If a handle selector is provided, check if the click is within the handle
    if (dragHandleSelector && panelRef.current) {
      const handle = panelRef.current.querySelector(dragHandleSelector);
      if (handle && !handle.contains(e.target as Node)) {
        return; // Clicked outside handle
      }
    }
    
    // Don't drag if clicking interactive elements (inputs, buttons)
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'BUTTON' || target.tagName === 'TEXTAREA') {
      return;
    }
    // Don't drag if clicking resize handle
    if (target.classList.contains('resize-handle')) {
      return;
    }

    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      w: size.width || panelRef.current?.offsetWidth || 200,
      h: size.height || panelRef.current?.offsetHeight || 100
    });
  };

  const panelStyle: React.CSSProperties = {
    position: 'absolute',
    left: position.x,
    top: position.y,
    cursor: isDragging ? 'grabbing' : (dragHandleSelector ? 'default' : 'grab'),
    userSelect: 'none',
    zIndex: (isDragging || isResizing) ? 1000 : style?.zIndex || 100,
    ...style
  };

  // Apply size if defined
  if (size.width) panelStyle.width = size.width;
  if (size.height) panelStyle.height = size.height;

  return (
    <div
      ref={panelRef}
      className={className}
      style={panelStyle}
      onMouseDown={handleMouseDown}
    >
      {children}
      
      {/* Resize Handle (SE corner) */}
      {resizable && (
        <div
          className="resize-handle"
          onMouseDown={handleResizeMouseDown}
          style={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            width: '20px',
            height: '20px',
            cursor: 'nwse-resize',
            // background: 'linear-gradient(135deg, transparent 50%, rgba(255, 107, 0, 0.6) 50%)', // Removed visible triangle
            background: 'transparent', // Invisible handle, cursor only
            zIndex: 10
          }}
          title="Drag to resize"
        />
      )}
    </div>
  );
};
